/**
 * ShiftPayRatesPage (BON-245) — the four flat per-shift rates, admin-editable.
 *
 * Saving does NOT overwrite a value: it schedules a new rate from a date. The
 * UI says so plainly, because "edit this number" and "this number changes on
 * this date going forward" behave differently and an admin who assumes the
 * former will be surprised when past pay doesn't move.
 *
 * Pay DISPLAY (what a given shift is worth) is BON-247 — this surface only
 * administers the rates.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  fetchShiftPayRates,
  setShiftPayRate,
  SHIFT_RATE_KEYS,
  SHIFT_RATE_LABELS,
  type ShiftRateEntry,
  type ShiftRateKey,
} from '../../api/shiftPayRates';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

const RATES_KEY = ['settings', 'shiftPayRates'];

/** Cents → "$250.00". */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** "250" / "250.50" → cents. Returns null when it isn't a sane amount. */
export function parseDollars(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * 100);
}

function RateRow({
  entry,
  canEdit,
  onSave,
  busy,
}: {
  entry: ShiftRateEntry;
  canEdit: boolean;
  onSave: (rateKey: ShiftRateKey, amountCents: number) => void;
  busy: boolean;
}) {
  const [draft, setDraft] = useState('');
  const editing = draft !== '';
  const parsed = editing ? parseDollars(draft) : null;
  const invalid = editing && parsed === null;

  return (
    <tr>
      <td>{SHIFT_RATE_LABELS[entry.rateKey]}</td>
      <td className="nb-tabular nb-mono">{formatCents(entry.amountCents)}</td>
      <td className="nb-muted">since {entry.effectiveFromIso}</td>
      {canEdit && (
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <input
            aria-label={`New ${SHIFT_RATE_LABELS[entry.rateKey]} rate`}
            className="nb-input"
            style={{ width: 96, marginRight: 8 }}
            placeholder="e.g. 250"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <button
            type="button"
            className="nb-btn nb-btn-primary"
            disabled={busy || !editing || invalid}
            onClick={() => {
              if (parsed !== null) {
                onSave(entry.rateKey, parsed);
                setDraft('');
              }
            }}
          >
            Save
          </button>
          {invalid && (
            <div role="alert" style={{ fontSize: 11, color: 'var(--nb-warn)' }}>
              Enter an amount like 250 or 250.50
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

export default function ShiftPayRatesPage() {
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.UPDATE_SETTINGS);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({ queryKey: RATES_KEY, queryFn: fetchShiftPayRates, staleTime: 30_000 });

  const save = useMutation({
    mutationFn: setShiftPayRate,
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
    onError: (err: Error) => setError(err.message),
  });

  if (q.isLoading) return <div style={{ fontSize: 12 }}>Loading rates…</div>;
  if (q.isError) {
    return (
      <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
        {(q.error as Error).message}
      </div>
    );
  }

  const rates = q.data ?? [];
  // Fixed order so the table never reshuffles; the API returns them ordered
  // too, but the page shouldn't depend on that.
  const ordered = SHIFT_RATE_KEYS.map((k) => rates.find((r) => r.rateKey === k)).filter(
    (r): r is ShiftRateEntry => Boolean(r),
  );

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 720 }}>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>
        Flat rates paid per workshop shift. Saving a new amount takes effect{' '}
        <strong>from today forward</strong> — pay already shown for past shifts keeps the rate that
        was in force then.
      </p>

      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
          {error}
        </div>
      )}

      <table className="nb-tbl">
        <thead>
          <tr>
            <th>Role</th>
            <th>Rate</th>
            <th>In force</th>
            {canEdit && <th></th>}
          </tr>
        </thead>
        <tbody>
          {ordered.map((entry) => (
            <RateRow
              key={entry.rateKey}
              entry={entry}
              canEdit={canEdit}
              busy={save.isPending}
              onSave={(rateKey, amountCents) => save.mutate({ rateKey, amountCents })}
            />
          ))}
        </tbody>
      </table>

      {!canEdit && (
        <p style={{ margin: 0, fontSize: 11, color: 'var(--nb-faint)' }}>
          Only admins can change pay rates.
        </p>
      )}
    </div>
  );
}
